/**
 * NavigationContext
 *
 * Manages sidebar state (open/closed, collapsed/expanded).
 * Handles responsive behavior for mobile vs desktop.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  NavigationContextValue,
  NavigationProviderProps,
  SidebarVariant,
} from '../../types';
import { MOBILE_BREAKPOINT } from '../../constants';

/**
 * Storage key for persisting collapsed state
 */
const COLLAPSED_STORAGE_KEY = 'navigation-sidebar-collapsed';

/**
 * Navigation Context
 */
const NavigationContext = createContext<NavigationContextValue | undefined>(
  undefined
);

/**
 * NavigationProvider Component
 *
 * Provides sidebar state management to the application.
 * Handles responsive behavior and optional persistence.
 *
 * @example
 * ```tsx
 * <NavigationProvider defaultCollapsed={false} persistCollapsed>
 *   <App />
 * </NavigationProvider>
 * ```
 */
export function NavigationProvider({
  children,
  defaultCollapsed = false,
  persistCollapsed = true,
}: NavigationProviderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));

  // State
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (persistCollapsed && typeof window !== 'undefined') {
      const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      return stored !== null ? stored === 'true' : defaultCollapsed;
    }
    return defaultCollapsed;
  });

  // Update open state when switching between mobile/desktop
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  // Persist collapsed state
  useEffect(() => {
    if (persistCollapsed && typeof window !== 'undefined') {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
    }
  }, [sidebarCollapsed, persistCollapsed]);

  // Actions
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const openSidebar = useCallback(() => {
    setSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  }, []);

  // Derived state
  const sidebarVariant: SidebarVariant = useMemo(() => {
    return isMobile ? 'temporary' : 'permanent';
  }, [isMobile]);

  // Context value
  const contextValue = useMemo<NavigationContextValue>(
    () => ({
      sidebarOpen,
      sidebarCollapsed,
      toggleSidebar,
      openSidebar,
      closeSidebar,
      toggleCollapsed,
      setCollapsed,
      isMobile,
      sidebarVariant,
    }),
    [
      sidebarOpen,
      sidebarCollapsed,
      toggleSidebar,
      openSidebar,
      closeSidebar,
      toggleCollapsed,
      setCollapsed,
      isMobile,
      sidebarVariant,
    ]
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
}

/**
 * useNavigation Hook
 *
 * Access the navigation context.
 *
 * @throws Error if used outside NavigationProvider
 *
 * @example
 * ```tsx
 * function Header() {
 *   const { toggleSidebar, isMobile } = useNavigation();
 *
 *   return (
 *     <AppBar>
 *       {isMobile && (
 *         <IconButton onClick={toggleSidebar}>
 *           <MenuIcon />
 *         </IconButton>
 *       )}
 *     </AppBar>
 *   );
 * }
 * ```
 */
export function useNavigation(): NavigationContextValue {
  const context = useContext(NavigationContext);

  if (context === undefined) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }

  return context;
}

export { NavigationContext };
